import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat
} from '@mui/icons-material';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

interface SparklineData {
  value: number;
}

interface EnhancedMetricCardProps {
  title: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  trend?: number; // Percentage change
  trendDirection?: 'up' | 'down' | 'flat';
  sparklineData?: SparklineData[];
  suffix?: string;
  onClick?: () => void;
}

const EnhancedMetricCard: React.FC<EnhancedMetricCardProps> = ({
  title,
  value,
  color,
  icon,
  trend,
  trendDirection = 'flat',
  sparklineData = [],
  suffix = '',
  onClick
}) => {
  const getTrendIcon = () => {
    switch (trendDirection) {
      case 'up':
        return <TrendingUp sx={{ fontSize: 16 }} />;
      case 'down':
        return <TrendingDown sx={{ fontSize: 16 }} />;
      default:
        return <TrendingFlat sx={{ fontSize: 16 }} />;
    }
  };

  const getTrendColor = () => {
    switch (trendDirection) {
      case 'up':
        return 'success';
      case 'down':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
              {value.toLocaleString()}{suffix}
            </Typography>

            {/* Trend Indicator */}
            {trend !== undefined && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Chip
                  icon={getTrendIcon()}
                  label={`${trend > 0 ? '+' : ''}${trend.toFixed(1)}%`}
                  size="small"
                  color={getTrendColor()}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Typography variant="caption" color="textSecondary">
                  vs last period
                </Typography>
              </Box>
            )}
          </Box>

          {/* Icon */}
          <Avatar
            sx={{
              bgcolor: color,
              width: 56,
              height: 56
            }}
          >
            {icon}
          </Avatar>
        </Box>

        {/* Sparkline */}
        {sparklineData.length > 0 && (
          <Box mt={2} height={50}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparklineData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  animationDuration={1000}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedMetricCard;
